/* ═══════════════════════════════════════════
   CONTACT EDITORIAL — tabs, autogrow, validation, submit state
   Ported from the redmenace theme. Two forms on one page
   (Feedback / Orders). Tab clicks animate panel swaps and an
   underline indicator that slides between tab labels.

   Wrapped so it can initialise every [data-contact-root] on the
   page and re-initialise when Shopify's theme editor reloads the
   section (shopify:section:load).
   ═══════════════════════════════════════════ */
(function () {
  'use strict';

  function initContactEditorial(root) {
    if (!root || root.dataset.contactInit === 'true') return;
    root.dataset.contactInit = 'true';

    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-contact-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-contact-panel]'));
    var indicator = root.querySelector('[data-contact-tab-indicator]');

    /* ── Textarea autogrow ── */
    function autogrow(ta) {
      ta.style.height = 'auto';
      ta.style.height = ta.scrollHeight + 'px';
    }
    root.querySelectorAll('.contact-editorial__input--textarea').forEach(function (ta) {
      ta.addEventListener('input', function () { autogrow(ta); });
      autogrow(ta);
    });

    /* ── Tab indicator positioning ── */
    function positionIndicator(targetTab, animate) {
      if (!indicator || !targetTab) return;
      var tabsRect = targetTab.parentElement.getBoundingClientRect();
      var tabRect = targetTab.getBoundingClientRect();
      var left = tabRect.left - tabsRect.left;
      var width = tabRect.width;

      if (!animate) {
        var prev = indicator.style.transition;
        indicator.style.transition = 'none';
        indicator.style.width = width + 'px';
        indicator.style.transform = 'translateX(' + left + 'px)';
        // Force reflow so subsequent changes animate again
        indicator.offsetHeight;
        indicator.style.transition = prev;
      } else {
        indicator.style.width = width + 'px';
        indicator.style.transform = 'translateX(' + left + 'px)';
      }
    }

    function getActiveTab() {
      return tabs.find(function (t) { return t.classList.contains('is-active'); });
    }

    /* Place indicator on first paint under the currently-active tab */
    function initIndicator() {
      var active = getActiveTab();
      if (active) positionIndicator(active, false);
    }
    initIndicator();
    // Reposition on resize (tabs may shift width if labels change)
    window.addEventListener('resize', function () {
      var active = getActiveTab();
      if (active) positionIndicator(active, false);
    });

    /* ── Switch tabs with a tight fade/slide ──
       shouldFocus: only true when user explicitly clicks a tab.
       On hash-sync / load, we skip focus so Safari doesn't auto-surface
       its autofill overlay and plant the user's email in the field. */
    function switchTo(name, shouldFocus) {
      var currentPanel = panels.find(function (p) { return p.dataset.contactPanel !== name && !p.hidden; });
      var nextPanel    = panels.find(function (p) { return p.dataset.contactPanel === name; });
      if (!nextPanel || nextPanel === currentPanel) return;

      // Update tabs
      tabs.forEach(function (t) {
        var isActive = t.dataset.contactTab === name;
        t.classList.toggle('is-active', isActive);
        t.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      // Slide indicator
      var nextTab = tabs.find(function (t) { return t.dataset.contactTab === name; });
      if (nextTab) positionIndicator(nextTab, true);

      // Update URL so a reload or share keeps the selected tab
      try {
        var url = new URL(window.location.href);
        if (name === 'feedback') url.searchParams.delete('form');
        else url.searchParams.set('form', name);
        window.history.replaceState({}, '', url);
      } catch (e) { /* ignore */ }

      // Animate panel swap
      if (currentPanel) {
        currentPanel.classList.remove('is-active');
        currentPanel.classList.add('is-leaving');
        setTimeout(function () {
          currentPanel.hidden = true;
          currentPanel.classList.remove('is-leaving');

          nextPanel.hidden = false;
          nextPanel.classList.add('is-entering');
          // Force reflow so the entering state applies before the active class
          nextPanel.offsetHeight;
          nextPanel.classList.remove('is-entering');
          nextPanel.classList.add('is-active');

          // Recompute autogrow for the now-visible textarea
          nextPanel.querySelectorAll('.contact-editorial__input--textarea').forEach(autogrow);
          // Only focus when the user explicitly clicked a tab
          if (shouldFocus) {
            var first = nextPanel.querySelector('.contact-editorial__input');
            if (first) first.focus({ preventScroll: true });
          }
        }, 180);
      } else {
        nextPanel.hidden = false;
        nextPanel.classList.add('is-active');
      }
    }

    tabs.forEach(function (t) {
      t.addEventListener('click', function () {
        switchTo(t.dataset.contactTab, true);
      });
    });

    /* ── On load: honour URL hash set by Shopify after form submit ──
       Shopify redirects to /pages/contact#FeedbackForm (or #OrdersForm)
       after processing — we use that to open the correct tab client-side
       regardless of what the initial server render chose. No focus here:
       Safari's autofill would open an overlay on load, which is annoying. */
    (function syncFromHash() {
      var h = window.location.hash;
      if (h === '#OrdersForm') {
        var onOrders = tabs.some(function (t) {
          return t.dataset.contactTab === 'orders' && t.classList.contains('is-active');
        });
        if (!onOrders) switchTo('orders', false);
      } else if (h === '#FeedbackForm') {
        var onFeedback = tabs.some(function (t) {
          return t.dataset.contactTab === 'feedback' && t.classList.contains('is-active');
        });
        if (!onFeedback) switchTo('feedback', false);
      }
    })();

    // Keyboard: left/right arrows between tabs
    var tablist = root.querySelector('.contact-editorial__tabs');
    if (tablist) {
      tablist.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        var active = getActiveTab();
        var idx = tabs.indexOf(active);
        var next = e.key === 'ArrowRight'
          ? tabs[(idx + 1) % tabs.length]
          : tabs[(idx - 1 + tabs.length) % tabs.length];
        if (next) { next.focus(); switchTo(next.dataset.contactTab, true); }
      });
    }

    /* ── Inline validation (quiet — only on blur, only if touched) ── */
    var emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    function validate(field) {
      var rule = field.dataset.validate;
      if (!rule) return { ok: true };
      var val = (field.value || '').trim();
      if (rule === 'required' && !val) return { ok: false };
      if (rule === 'email') {
        if (!val) return { ok: false };
        if (!emailRe.test(val)) return { ok: false };
      }
      return { ok: true };
    }

    function applyState(field, result) {
      var wrap = field.closest('.contact-editorial__field');
      if (!wrap) return;
      wrap.classList.toggle('has-error', !result.ok);
      wrap.classList.toggle('is-ok', result.ok);
    }

    root.querySelectorAll('[data-validate]').forEach(function (field) {
      field.addEventListener('blur', function () {
        if (!field.value) return;
        applyState(field, validate(field));
      });
      field.addEventListener('input', function () {
        var wrap = field.closest('.contact-editorial__field');
        if (wrap && wrap.classList.contains('has-error')) {
          applyState(field, validate(field));
        }
      });
    });

    /* ── Submit: validate the submitted form's fields only ── */
    root.querySelectorAll('.contact-editorial__form').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        var firstInvalid = null;
        form.querySelectorAll('[data-validate]').forEach(function (field) {
          var res = validate(field);
          applyState(field, res);
          if (!res.ok && !firstInvalid) firstInvalid = field;
        });
        if (firstInvalid) {
          e.preventDefault();
          firstInvalid.focus();
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        var btn = form.querySelector('[data-contact-submit]');
        var lbl = form.querySelector('[data-submit-label]');
        if (btn) btn.classList.add('is-sending');
        if (lbl) lbl.textContent = 'Sending';
      });
    });

    /* ── Success acknowledgement: scroll into view on server reload ── */
    var ack = root.querySelector('.contact-editorial__ack');
    if (ack) {
      setTimeout(function () {
        ack.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 60);
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-contact-root]').forEach(initContactEditorial);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Theme editor: re-initialise when this section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
